import type {
  Ambassador,
  TypedBaseBlog,
  TypedBaseProduct,
} from '@bulknutrients/bulk-shared-lib/interfaces/Ambassador';
import type { MetaData } from '@bulknutrients/bulk-shared-lib/interfaces/MetaData';
import type { BaseProduct } from '@bulknutrients/bulk-shared-lib/interfaces/Product';
import {
  getAllAmbassadorSlugs,
  getAmbassadorData,
  getAmbassadorMetaData,
  getPreviewAmbassadorData,
} from '@bulknutrients/bulk-shared-lib/lib/sanity/ambassadors';
import { addBigCommerceDataToBaseProducts } from '@bulknutrients/bulk-shared-lib/lib/sanity/products/transform';
import { getRedirect } from '@bulknutrients/bulk-shared-lib/lib/sanity/redirect';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { Container } from '@components/Container';
import { HorizontalDivider } from '@components/HorizontalDivider/HorizontalDivider';
import { StandardLayout } from '@components/Layouts';
import { getImageSrcUrl } from '@components/Media/Image/Image';
import { PortableTextBlockRenderer } from '@components/PortableText/PortableText';
import { SocialShares } from '@components/Social';
import { Paragraph } from '@components/Typography/Paragraph/Paragraph';
import { useLocalStorage } from '@hooks/useLocalStorage';
import type { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';
import { Fragment, type ReactElement, type ReactNode, useEffect } from 'react';

const RecommendationsRenderer = {
  block: {
    normal: ({ children }: { children?: ReactNode }) => (
      <Paragraph className="col-span-full">{children}</Paragraph>
    ),
    h2: ({ children }: { children?: ReactNode }) => (
      <h2 className="col-span-full font-bold italic">{children}</h2>
    ),
  },
};

export default function CategoryPage({
  ambassador,
  breadcrumbs,
  meta,
  isPreview,
}: {
  ambassador: Ambassador;
  breadcrumbs: { title: string; path: string }[];
  meta: MetaData;
  isPreview: boolean;
}): ReactElement {
  const { setRecentlyViewedPage } = useLocalStorage();

  useEffect(() => {
    setRecentlyViewedPage({
      page: { slug: ambassador.slug, title: ambassador.name },
      viewedTime: Date.now(),
    });
  }, [ambassador, setRecentlyViewedPage]);

  return (
    <>
      <Head>
        <title>{meta.metaTitle}</title>
        <meta name="title" content={meta.metaTitle} />
        <meta name="description" content={meta.metaDescription} />
        {meta.noIndex && <meta name="robots" content="noindex" />}
        <link
          rel="canonical"
          href={
            meta.canonical
              ? meta.canonical
              : `${process.env.NEXT_PUBLIC_SERVER_URL}${ambassador.slug}`
          }
        />
      </Head>
      <Container>
        <Breadcrumbs crumbs={breadcrumbs} />
      </Container>
      <Container
        as="main"
        id="main-content"
        className="grid-cols-[1fr_min(var(--width-narrow-page),calc(100%-16px))_1fr]"
      >
        {ambassador.content.map((block) =>
          block.contentDescription === 'Recommendations' ? (
            <Fragment key={block._key}>
              <PortableTextBlockRenderer
                content={block.contentBody}
                componentsOverride={RecommendationsRenderer}
                customClass="grid grid-cols-1 gap-2.5 sm:grid-cols-2"
              />
              <HorizontalDivider />
            </Fragment>
          ) : (
            <Fragment key={block._key}>
              <PortableTextBlockRenderer content={block.contentBody} />
            </Fragment>
          )
        )}
        <div className="flex justify-center">
          <SocialShares
            type={'Bulk Ambassador'}
            shareSlug={ambassador.slug}
            titleText={ambassador.name}
            imageSource={ambassador.thumbnailImgUrl}
          />
        </div>
      </Container>
    </>
  );
}

CategoryPage.getLayout = function getLayout(page: ReactElement) {
  return <StandardLayout>{page}</StandardLayout>;
};

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = await getAllAmbassadorSlugs();

  return {
    paths,
    fallback: 'blocking',
  };
};

function isProductCarousel(
  item: TypedBaseProduct | TypedBaseBlog | BaseProduct
): item is TypedBaseProduct {
  return (item as TypedBaseProduct)._type === 'product';
}

export const getStaticProps: GetStaticProps = async ({
  params,
  preview,
  previewData,
}) => {
  let ambassador: Ambassador;
  if (preview) {
    // This is preview mode
    ambassador = await getPreviewAmbassadorData(
      (previewData as { id: string }).id
    );
  } else {
    ambassador = await getAmbassadorData(
      `shop-by-recommended,${params?.ambassador?.toString() as string}`
    );
  }

  if (!ambassador) {
    // try redirect
    const redirect = await getRedirect(
      `/categories/shop-by-recommended/${params?.ambassador?.toString()}` as string
    );
    if (redirect) {
      return {
        redirect: {
          destination: redirect.newUrl,
          permanent: redirect.permanent,
        },
      };
    }

    // fallback failed
    return {
      notFound: true,
    };
  }

  // If there is a product carousel, add the BC data that is needed for a BaseProduct
  // Get all products out
  const products = [] as Array<any>;
  ambassador.content.forEach((content) => {
    content.contentBody.forEach((block) => {
      if (
        block._type === 'carousel' &&
        block.items &&
        isProductCarousel(block.items[0])
      ) {
        products.push(...block.items);
      }
    });
  });

  // Map products
  const mappedProducts = await addBigCommerceDataToBaseProducts(products);

  // Replace them
  ambassador.content.forEach((content) => {
    if (content.contentBody) {
      content.contentBody = content.contentBody.map((block) => {
        if (
          block._type === 'carousel' &&
          block.items &&
          isProductCarousel(block.items[0])
        ) {
          const mappedItems = block.items.reduce(
            (acc, item) => {
              const product = mappedProducts.find(
                (p) => p.entityId === (item as TypedBaseProduct).entityId
              );
              if (product) acc.push(product);
              return acc;
            },
            [] as Array<BaseProduct>
          );
          return {
            ...block,
            items: mappedItems,
          };
        }
        return block;
      });
    }
  });

  const breadcrumbs = [
    { title: 'home', path: '/' },
    { title: 'Shop by recommended', path: '/categories/shop-by-recommended' },
    { title: ambassador.name, path: ambassador.slug },
  ];

  const meta = await getAmbassadorMetaData(ambassador._id);

  // Build the full url of the thumbnail image for sharing
  const thumbnailSrc = getImageSrcUrl({
    image: ambassador.thumbnail,
    width: 500,
  });
  ambassador.thumbnailImgUrl = thumbnailSrc;

  return {
    props: {
      ambassador,
      breadcrumbs,
      meta,
      isPreview: !!preview,
    },
  };
};
